import { inject, Injectable } from '@angular/core';
import { Observable, of, Subscriber } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import * as qz from 'qz-tray';

import { ConnectionsService } from '../edit-create';
import { Connections } from '../../models';

@Injectable({
  providedIn: 'root',
})
export class PerchDeviceService {
  private readonly connectionsService = inject(ConnectionsService);

  private connectionCache = new Map<string, Connections>();

  preset(connectionId: string, amount: number): Observable<void> {
    return this.getConnection(connectionId).pipe(
      switchMap((connection) => {
        const body = `${encodeURIComponent('"Webdata".Preset')}=${amount.toFixed(2)}`;
        return this.sendHttpPost(connection, body);
      }),
    );
  }

  reset(connectionId: string): Observable<void> {
    return this.getConnection(connectionId).pipe(
      switchMap((connection) => {
        const body = `${encodeURIComponent('"Webdata".Reset')}=1`;
        return this.sendHttpPost(connection, body);
      }),
    );
  }

  private getConnection(connectionId: string): Observable<Connections> {
    const cached = this.connectionCache.get(connectionId);
    if (cached) {
      return of(cached);
    }

    return this.connectionsService
      .edit(connectionId)
      .pipe(tap((connection) => this.connectionCache.set(connectionId, connection)));
  }

  private parseConnection(connection: Connections): { host: string; port: number; path: string } {
    const port = connection.port || 80;

    try {
      const url = new URL(connection.ipAddress);
      const path = url.pathname && url.pathname !== '/' ? url.pathname : '/';
      return { host: url.hostname, port, path };
    } catch {
      return { host: connection.ipAddress, port, path: '/' };
    }
  }

  private sendHttpPost(connection: Connections, body: string): Observable<void> {
    const { host, port, path } = this.parseConnection(connection);

    const rawRequest =
      `POST ${path} HTTP/1.1\r\n` +
      `Host: ${host}:${port}\r\n` +
      'Content-Type: application/x-www-form-urlencoded\r\n' +
      `Content-Length: ${body.length}\r\n` +
      'Connection: close\r\n' +
      '\r\n' +
      body;

    return new Observable((observer: Subscriber<void>) => {
      qz.websocket
        .connect()
        .then(() => qz.socket.open(host, port))
        .then(() => qz.socket.sendData(host, port, rawRequest))
        .then(() => qz.socket.close(host, port))
        .then(() =>
          qz.websocket.disconnect().then(() => {
            observer.next();
            observer.complete();
          }),
        )
        .catch((e: any) =>
          qz.websocket.disconnect().then(() => {
            observer.error(e);
          }),
        );
    });
  }
}
